import React, { useState, useMemo, useEffect } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import yaml from 'js-yaml';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  toggleParagraphSelection,
  selectParagraphIds,
  selectParagraphCount,
  selectIsParagraphSelected,
  clearDocumentSelections,
  MAX_PARAGRAPH_SELECTIONS,
} from "./DraftSelectionSlice_Store";
import { selectActiveDocument } from "./DocumentPreviewSlice_Store";
import { selectCurrentProjectId } from "./ProjectSlice_Store";
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";

// Configuration - Toggle visibility of badges
const BADGE_CONFIG = {
  showIndexBadge: false,
  showTypeBadge: false,
  showSectionBadge: false,
  showStyleBadge: false,
  showFixedBadge: false,
};

// Types
interface Recipient {
  name: string;
  role?: string;
}

interface RecipientData {
  recipients?: Recipient[];
  bank?: string;
}

interface Signatory {
  name: string;
  title: string;
}

interface SignatureData {
  closing_text?: string;
  signatories?: Signatory[];
  on_behalf?: string;
}

interface Advisor {
  item: string;
  firm: string;
}

interface AdvisorsTableData {
  title?: string;
  intro?: string;
  advisors?: Advisor[];
}

interface Contact {
  name: string;
  role: string;
  email: string;
  phone: string;
}

interface ContactsTableData {
  title?: string;
  intro?: string;
  contacts?: Contact[];
}

type ParagraphData = string | RecipientData | SignatureData | AdvisorsTableData | ContactsTableData;

interface Paragraph {
  type?: 'markdown' | 'recipients' | 'signatures' | 'advisors_table' | 'contacts_table';
  data: ParagraphData;
  style?: string;
  section_id?: string;
  fixed?: boolean;
}

interface DraftContentData {
  paragraphs: Paragraph[];
}

interface DraftContentPreviewProps {
  contentText: string;
}

interface ParagraphItemProps {
  paragraph: Paragraph;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  projectId: string | undefined;
  docId: string | undefined;
  onToggleSelection: (paragraphId: number, isSelected: boolean) => void;
}

interface ParagraphContentProps {
  paragraph: Paragraph;
}

/**
 * DraftContentPreview Component
 * Renders draft_content.yaml with paragraph-based document structure
 * Supports various paragraph types: markdown, recipients, signatures, advisors_table, contacts_table
 */
export default function DraftContentPreview_Component({ contentText }: DraftContentPreviewProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<number>>(new Set());
  const [showLimitWarning, setShowLimitWarning] = useState<boolean>(false);

  // Get current project and document
  const projectId = useAppSelector(selectCurrentProjectId);
  const activeDocument = useAppSelector(selectActiveDocument);
  const docId = activeDocument?.docId;

  // Get selected paragraph IDs from Redux
  const selectedCount = useAppSelector(selectParagraphCount(projectId, docId));

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo(() => {
    try {
      const data = yaml.load(contentText) as DraftContentData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  // Initialize expanded paragraphs to show all expanded by default
  useEffect(() => {
    if (parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    }
  }, [parsedData]);

  const toggleParagraph = (index: number): void => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedParagraphs(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    } else {
      setExpandedParagraphs(new Set());
    }
  };

  // Selection handlers
  const handleToggleParagraphSelection = (paragraphId: number, isCurrentlySelected: boolean): void => {
    if (!projectId || !docId) {
      console.warn('[DraftContent] Missing projectId or docId:', { projectId, docId });
      return;
    }

    // If trying to select (not deselect) and already at limit
    if (!isCurrentlySelected && selectedCount >= MAX_PARAGRAPH_SELECTIONS) {
      setShowLimitWarning(true);
      setTimeout(() => {
        setShowLimitWarning(false);
      }, 5000);
      return;
    }

    dispatch(toggleParagraphSelection({ projectId, docId, paragraphId: String(paragraphId) }));
  };

  const handleClearSelections = (): void => {
    if (projectId && docId) {
      dispatch(clearDocumentSelections({ projectId, docId }));
    }
  };

  // Error state - YAML parsing failed
  if (parseError) {
    return (
      <div className="h-full overflow-auto p-6 bg-cvc-canvas">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  Invalid YAML Format
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  The content YAML file could not be parsed. Please check the file format.
                </p>
                <pre className="text-xs text-red-600 bg-red-100 p-3 rounded font-mono overflow-x-auto">
                  {parseError}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No paragraphs structure - fall back to raw YAML display
  const hasParagraphs = parsedData && Array.isArray(parsedData.paragraphs);

  if (!hasParagraphs) {
    return (
      <div className="h-full overflow-auto p-6 bg-cvc-canvas">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  No Paragraphs Found
                </h4>
                <p className="text-xs text-yellow-700">
                  This content file doesn't have the expected "paragraphs" array. Showing raw content below.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <pre className="text-xs text-gray-700 font-mono leading-relaxed whitespace-pre-wrap">
              {contentText}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // Render structured document paragraphs
  return (
    <div className="h-full overflow-auto bg-cvc-canvas">
      {/* Toast notification - fixed position in top-right */}
      {showLimitWarning && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 shadow-lg max-w-md">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium text-orange-800">
                Only {MAX_PARAGRAPH_SELECTIONS} paragraphs can be selected at a time for requesting an update
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center space-x-2 min-w-0">
                <DocumentTextIcon className="h-5 w-5 text-cvc-blue flex-shrink-0" />
                <h2 className="text-base font-semibold text-gray-900 whitespace-nowrap">Draft Content</h2>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  ({parsedData.paragraphs.length} paragraphs)
                </span>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => toggleAll(true)}
                  className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => toggleAll(false)}
                  className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {/* Selection controls */}
            <div className="flex items-center justify-between bg-cvc-lb2 border border-cvc-lb1 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">
                  {selectedCount > 0 ? (
                    <span className="text-cvc-blue">
                      {selectedCount} paragraph{selectedCount !== 1 ? 's' : ''} selected (max {MAX_PARAGRAPH_SELECTIONS})
                    </span>
                  ) : (
                    <span className="text-gray-500">No paragraphs selected (max {MAX_PARAGRAPH_SELECTIONS})</span>
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {selectedCount > 0 && (
                  <button
                    onClick={handleClearSelections}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear Selection
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {parsedData.paragraphs.map((paragraph, index) => (
              <ParagraphItem
                key={index}
                paragraph={paragraph}
                index={index}
                isExpanded={expandedParagraphs.has(index)}
                onToggle={() => toggleParagraph(index)}
                projectId={projectId}
                docId={docId}
                onToggleSelection={handleToggleParagraphSelection}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Collapsible Paragraph Item
 */
function ParagraphItem({
  paragraph,
  index,
  isExpanded,
  onToggle,
  projectId,
  docId,
  onToggleSelection
}: ParagraphItemProps): JSX.Element {
  const { type, data, style, section_id, fixed } = paragraph;
  const paragraphType = type || 'markdown';
  const paragraphId = index;

  // Get selection state from Redux
  const isSelected = useAppSelector(selectIsParagraphSelected(projectId, docId, String(paragraphId)));

  // Handle checkbox click
  const handleCheckboxClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onToggleSelection(paragraphId, isSelected);
  };

  // Get preview text (first 100 chars)
  const getPreviewText = (): string => {
    if (paragraphType === 'markdown') {
      const text = typeof data === 'string' ? data : String(data || '');
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    }
    if (paragraphType === 'recipients') {
      const recipientData = data as RecipientData;
      return 'For the attention of: ' + (recipientData?.recipients?.map(r => r.name).join(', ') || 'Recipients');
    }
    if (paragraphType === 'signatures') {
      const signatureData = data as SignatureData;
      return 'Signatures: ' + (signatureData?.signatories?.map(s => s.name).join(', ') || 'Signatures');
    }
    if (paragraphType === 'advisors_table') {
      const advisorData = data as AdvisorsTableData;
      return advisorData?.title || 'Advisors Table';
    }
    if (paragraphType === 'contacts_table') {
      const contactData = data as ContactsTableData;
      return contactData?.title || 'Contacts Table';
    }
    return `${paragraphType} content`;
  };

  return (
    <div className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
      <div className="flex items-start p-4">
        {/* Checkbox for selection */}
        <div
          className="flex-shrink-0 p-1 -m-1 cursor-pointer"
          onClick={handleCheckboxClick}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            title={`Select paragraph #${paragraphId}`}
            style={{ pointerEvents: 'none' }}
          />
        </div>

        {/* Expand/collapse icon */}
        <div onClick={onToggle} className="flex-shrink-0 cursor-pointer mx-3">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          )}
        </div>

        {/* Content area - full width */}
        <div className="flex-1 min-w-0">
          {/* Header with badges - clickable to expand/collapse */}
          <div onClick={onToggle} className="cursor-pointer mb-2">
            <div className="flex items-center space-x-2">
              {BADGE_CONFIG.showIndexBadge && <span className="text-xs font-medium text-gray-500">#{index}</span>}
              {BADGE_CONFIG.showSectionBadge && section_id && <SectionBadge section={section_id} />}
              {BADGE_CONFIG.showTypeBadge && <TypeBadge type={paragraphType} />}
              {BADGE_CONFIG.showStyleBadge && style && <StyleBadge style={style} />}
              {BADGE_CONFIG.showFixedBadge && fixed && (
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                  fixed
                </span>
              )}
            </div>
          </div>

          {/* Show preview when collapsed, full content when expanded */}
          {!isExpanded ? (
            <div onClick={onToggle} className="text-sm text-gray-600 truncate cursor-pointer">
              {getPreviewText()}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <ParagraphContent paragraph={paragraph} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Section Badge Component
 */
function SectionBadge({ section }: { section: string }): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-teal-100 text-teal-700">
      {section}
    </span>
  );
}

/**
 * Type Badge Component
 */
function TypeBadge({ type }: { type: string }): JSX.Element {
  const colors: Record<string, string> = {
    markdown: 'bg-blue-100 text-blue-700',
    recipients: 'bg-purple-100 text-purple-700',
    signatures: 'bg-green-100 text-green-700',
    advisors_table: 'bg-orange-100 text-orange-700',
    contacts_table: 'bg-orange-100 text-orange-700',
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
      {type}
    </span>
  );
}

/**
 * Style Badge Component
 */
function StyleBadge({ style }: { style: string }): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
      {style}
    </span>
  );
}

/**
 * Paragraph Content Renderer
 */
function ParagraphContent({ paragraph }: ParagraphContentProps): JSX.Element {
  const { type, data } = paragraph;

  // Type: recipients
  if (type === 'recipients') {
    const recipientData = data as RecipientData;
    return (
      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-700 mb-2">For the attention of:</div>
        {recipientData.recipients && recipientData.recipients.map((recipient, idx) => (
          <div key={idx} className="text-gray-900">
            <div className="font-medium">{recipient.name}</div>
            {recipient.role && <div className="text-sm text-gray-600">{recipient.role}</div>}
          </div>
        ))}
        {recipientData.bank && (
          <div className="mt-2 text-gray-900">{recipientData.bank}</div>
        )}
      </div>
    );
  }

  // Type: signatures
  if (type === 'signatures') {
    const signatureData = data as SignatureData;
    return (
      <div className="space-y-4">
        <div className="text-sm font-semibold text-gray-700 mb-2">Signatures:</div>
        {signatureData.closing_text && (
          <div className="text-gray-700 prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{signatureData.closing_text}</ReactMarkdown>
          </div>
        )}
        <div className="space-y-3 pt-3 border-t border-gray-200">
          {signatureData.signatories && signatureData.signatories.map((signatory, idx) => (
            <div key={idx} className="space-y-0.5">
              <div className="font-medium text-gray-900">{signatory.name}</div>
              <div className="text-sm text-gray-600">{signatory.title}</div>
            </div>
          ))}
          {signatureData.on_behalf && (
            <div className="text-sm text-gray-600 italic mt-2">On behalf of {signatureData.on_behalf}</div>
          )}
        </div>
      </div>
    );
  }

  // Type: advisors_table
  if (type === 'advisors_table') {
    const advisorData = data as AdvisorsTableData;
    return (
      <div className="space-y-3">
        {advisorData.title && (
          <h4 className="font-semibold text-gray-900">{advisorData.title}</h4>
        )}
        {advisorData.intro && (
          <p className="text-sm text-gray-700">{advisorData.intro}</p>
        )}
        {advisorData.advisors && advisorData.advisors.length > 0 && (
          <div className="overflow-hidden border border-gray-300 rounded">
            <table className="min-w-full divide-y divide-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Firm</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {advisorData.advisors.map((advisor, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-gray-900">{advisor.item}</td>
                    <td className="px-3 py-2 text-gray-700">{advisor.firm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Type: contacts_table
  if (type === 'contacts_table') {
    const contactData = data as ContactsTableData;
    return (
      <div className="space-y-3">
        {contactData.title && (
          <h4 className="font-semibold text-gray-900">{contactData.title}</h4>
        )}
        {contactData.intro && (
          <p className="text-sm text-gray-700">{contactData.intro}</p>
        )}
        {contactData.contacts && contactData.contacts.length > 0 && (
          <div className="overflow-hidden border border-gray-300 rounded">
            <table className="min-w-full divide-y divide-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Role</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Phone</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contactData.contacts.map((contact, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 font-medium text-gray-900">{contact.name}</td>
                    <td className="px-3 py-2 text-gray-700">{contact.role}</td>
                    <td className="px-3 py-2 text-blue-600">
                      <a href={`mailto:${contact.email}`} className="hover:underline">
                        {contact.email}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{contact.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Type: markdown (default)
  const paragraphData = typeof data === 'string' ? data : String(data || '');

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({children}) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h1>,
          h2: ({children}) => <h2 className="text-lg font-bold text-gray-900 mt-3 mb-2">{children}</h2>,
          h3: ({children}) => <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{children}</h3>,
          p: ({children}) => <p className="text-gray-700 mb-2 leading-relaxed">{children}</p>,
          ul: ({children}) => <ul className="list-disc list-outside ml-6 mb-3 space-y-1 text-gray-700 marker:text-gray-900">{children}</ul>,
          ol: ({children}) => <ol className="list-decimal list-outside ml-6 mb-3 space-y-1 text-gray-700 marker:text-gray-900">{children}</ol>,
          li: ({children}) => <li className="leading-relaxed">{children}</li>,
          strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
          em: ({children}) => <em className="italic text-gray-700">{children}</em>,
          code: ({children, ...props}) => {
            const isInline = !props.className || !props.className.startsWith('language-');
            return isInline ?
              <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono text-gray-800">{children}</code> :
              <code className="block bg-gray-200 p-2 rounded text-xs font-mono text-gray-800 overflow-x-auto">{children}</code>;
          },
        }}
      >
        {paragraphData}
      </ReactMarkdown>
    </div>
  );
}
